import { ProjectionStatuses } from 'business/constants';
import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import { ResourceSoftwareIdField } from 'routes/BudgetModule/config/types';
import { HttpError, RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { dispatch } from 'store/store';
import { ProjectionT } from '../helpers/types';
import {
    GESTIONNAIRE_SOFTWARE_ID,
    ORG_CHART_FINANCE_SOFTWARE_ID,
} from 'routes/BudgetModule/config/constants';

/**
 * Hook to fetch a projection
 */
export const useGetProjection = (
    budgetProposalId: number,
    resourceSoftwareId: string,
): UseQueryResult<ProjectionT> => {
    const endpoint = `budget_proposals/projection/${budgetProposalId}/${encodeURIComponent(
        resourceSoftwareId,
    )}`;

    return useQuery({
        queryKey: ['projection', budgetProposalId, resourceSoftwareId],
        queryFn: async () => {
            const getResponse = await RQAPICallWithData<ProjectionT>({
                endpoint,
                method: 'GET',
            });
            return {
                ...getResponse.content.data,
            };
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};

/**
 * Hook to update the status of a projection
 */
export function useProjectionMutation({
    budgetProposalId,
    projectionId,
    resourceSoftwareId,
    statusField,
}: {
    budgetProposalId: number;
    projectionId: number;
    resourceSoftwareId: string;
    statusField: 'pluriannualProjectionStatus' | 'atterrissageStatus';
}): { mutate: (arg1: { status: ProjectionStatuses }) => void } {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: ({ status }: { status: ProjectionStatuses }) =>
            RQAPICallWithData<ProjectionT>({
                endpoint: `budget_proposals/projection/${projectionId}`,
                method: 'PATCH',
                body: { [statusField]: status },
            }),

        onSuccess: ({ content }): void => {
            dispatch.vision.notifyUser({
                message: content.message,
                snackType: 'success',
            });
            queryClient.setQueryData(
                ['projection', budgetProposalId, resourceSoftwareId],
                () => ({ ...content.data }),
            );
            queryClient.setQueryData(
                ['projections', budgetProposalId],
                (oldProjections: ProjectionT[]) => {
                    const updatedProjection = content.data;
                    if (!oldProjections) return [updatedProjection];
                    return oldProjections.map((projection) =>
                        projection.id === updatedProjection.id
                            ? updatedProjection
                            : projection,
                    );
                },
            );
        },

        meta: {
            useGlobalOnError: true,
        },
    });
    return mutation;
}

/**
 * Hook to fetch all projections for a budget proposal
 */
export const useGetProjectionsForBudgetProposal = ({
    budgetProposalId,
    enabled,
}: {
    budgetProposalId: number;
    enabled: boolean;
}): UseQueryResult<ProjectionT[], HttpError> => {
    const endpoint = `budget_proposals/projections/${budgetProposalId}`;

    return useQuery({
        queryKey: ['projections', budgetProposalId],
        queryFn: async () => {
            const getResponse = await RQAPICall({
                endpoint,
                method: 'GET',
            });
            return getResponse.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
        enabled,
    });
};

export type ProjectionResourceSoftwareId = Extract<
    keyof ProjectionT,
    'orgChartFinanceSoftwareId' | 'gestionnaireSoftwareId'
>;

export function extractSoftwareId(
    field: ResourceSoftwareIdField,
): ProjectionResourceSoftwareId {
    switch (field) {
        case ORG_CHART_FINANCE_SOFTWARE_ID:
            return 'orgChartFinanceSoftwareId';
        case GESTIONNAIRE_SOFTWARE_ID:
        default:
            return 'gestionnaireSoftwareId';
    }
}
